import React, { useContext, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { lightTheme } from '~/configs/leTheme';
import MUIProvider from '~/contexts/MUIProvider';

import { Box, CssBaseline, ThemeProvider } from '@mui/material';

import { UnifiedAnalyticsThemeProvider } from '~/pages/AskRichardUnified/UnifiedAnalyticsThemeProvider';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { PersonalViewActionsContext } from '~/pages/LeLens/personalViews/PersonalViewBoundary';
import type { LensPrintTarget } from '~/pages/LeLens/print/lensPrintTypes';

import type { LensFilterValues } from '~/types/leLens';

import NativeLensActions from './NativeLensActions';
import { nativeLensId } from './nativeLensId';
import { NativePrintMode, nativePrintSession } from './nativePrintSession';
import { useNativeLensPrint } from './useNativeLensPrint';

export type NativePrintProps = {
  printTarget?: LensPrintTarget;
  printFilters?: LensFilterValues;
};

type ReportState = { ready: boolean; error: string | null; filters: LensFilterValues };

function sameReport(left: ReportState, right: ReportState) {
  return left.ready === right.ready && left.error === right.error && left.filters === right.filters;
}

function filtersOf(key: ReadonlyArray<unknown>): LensFilterValues {
  const at = key.indexOf('data');
  const value = at >= 0 ? key[at + 1] : undefined;
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as LensFilterValues) : {};
}

function messageOf(error: unknown) {
  return error instanceof Error ? error.message : 'The report could not load.';
}

export function NativePrintChrome({
  printTarget,
  children,
}: {
  printTarget?: LensPrintTarget;
  children: React.ReactNode;
}) {
  if (!printTarget) return children;
  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

export function NativePrintBoard({
  pageId,
  title,
  printTarget,
  cards = 'board',
  allowScheduling = true,
  children,
}: {
  pageId: string;
  title: string;
  printTarget?: LensPrintTarget;
  cards?: 'board' | 'marked';
  allowScheduling?: boolean;
  children: React.ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const actionsTarget = useContext(PersonalViewActionsContext);
  const client = useQueryClient();
  const lensId = nativePrintSession()?.lensId ?? nativeLensId(pageId);
  const [report, setReport] = useState<ReportState>({ ready: false, error: null, filters: {} });
  useEffect(() => {
    const cache = client.getQueryCache();
    const sync = () => {
      const queries = cache.findAll({
        predicate: (query) =>
          query.getObserversCount() > 0 && query.queryKey.includes(lensId) && query.queryKey.includes('data'),
      });
      const filters = filtersOf(queries[0]?.queryKey ?? []);
      const fetching = queries.some(
        (query) => query.state.fetchStatus === 'fetching' || query.state.status === 'pending',
      );
      const failed = queries.find((query) => query.state.status === 'error');
      const incomplete = queries
        .flatMap(
          (query) =>
            (query.state.data as { cards?: Array<{ status: string; truncated?: boolean; message?: string }> })?.cards ??
            [],
        )
        .find((card) => ['error', 'restricted', 'unconfigured'].includes(card.status) || card.truncated);
      const next: ReportState = fetching
        ? { ready: false, error: null, filters }
        : failed || incomplete
          ? {
              ready: false,
              error: failed
                ? messageOf(failed.state.error)
                : (incomplete?.message ??
                  'The complete report is unavailable. Resolve its filters or row limit before exporting.'),
              filters,
            }
          : {
              ready: queries.some((query) => query.state.status === 'success'),
              error: null,
              filters,
            };
      setReport((current) => (sameReport(current, next) ? current : next));
    };
    sync();
    return cache.subscribe(sync);
  }, [client, lensId]);
  const marked = cards === 'marked' ? (root.current?.querySelectorAll('[data-lens-pdf-card]').length ?? 0) : 0;
  const expectedCardCount = cards === 'board' ? 1 : marked;
  const { printing, downloadPdf } = useNativeLensPrint({
    root,
    ready: report.ready && expectedCardCount > 0,
    error: report.error,
    target: printTarget,
    title,
    expectedCardCount,
  });
  const actions = (
    <Box data-lens-pdf-ignore="" sx={{ display: 'flex', justifyContent: 'flex-end', '& > button': { p: 0.5 } }}>
      <NativeLensActions
        allowScheduling={allowScheduling}
        pageId={pageId}
        filters={report.filters}
        filterOptions={{}}
        ready={report.ready && !printing}
        onDownloadPdf={downloadPdf}
      />
    </Box>
  );
  return (
    <NativePrintMode.Provider value={printing && !printTarget?.previewRows}>
      <Box ref={root} data-lens-print-fit="" sx={{ bgcolor: '#fff', color: 'text.primary' }}>
        {!printTarget && (actionsTarget ? createPortal(actions, actionsTarget) : actions)}
        <Box {...(cards === 'board' ? { 'data-lens-pdf-card': '', 'data-lens-pdf-card-type': 'table' } : {})}>
          {children}
        </Box>
      </Box>
    </NativePrintMode.Provider>
  );
}

export function NativeReportShell({
  pageId,
  title,
  printTarget,
  cards,
  analytics = false,
  allowScheduling = true,
  children,
}: {
  pageId: string;
  title: string;
  printTarget?: LensPrintTarget;
  cards?: 'board' | 'marked';
  allowScheduling?: boolean;
  analytics?: boolean;
  children: React.ReactNode;
}) {
  const report = (
    <NativePrintBoard
      pageId={pageId}
      title={title}
      printTarget={printTarget}
      cards={cards}
      allowScheduling={allowScheduling}
    >
      {children}
    </NativePrintBoard>
  );
  if (printTarget) return <NativePrintChrome printTarget={printTarget}>{report}</NativePrintChrome>;
  const laidOut = <PageChatLayout>{report}</PageChatLayout>;
  return (
    <MUIProvider>
      {analytics ? <UnifiedAnalyticsThemeProvider>{laidOut}</UnifiedAnalyticsThemeProvider> : laidOut}
    </MUIProvider>
  );
}
